import { useEffect, useState } from "react";
import "./UserProfile.scss";
import UserImg from "../../assets/images/vactor/placeholder-avatar.svg";
import CameraImgIcon from "../../assets/images/vactor/camera-icon.svg";
import EditTextIcon from "../../assets/images/vactor/edit-text-field.svg";
import CheckIcon from "../../assets/images/vactor/green-check-round.svg";
import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Stack,
  styled,
  Switch,
  TextField,
} from "@mui/material";
import {
  LightButton,
  PrimaryButton,
} from "../../components/AllButtons/AllButtons";
import dayjs, { Dayjs } from "dayjs";
import ChangePassword from "../../components/ChangePassword/ChangePassword";
import PasswordReset from "../../components/ChangePassword/PasswordReset";
import { RootState, useAppDispatch, useAppSelector } from "../../Store/store";
import {
  getUserDetails,
  updateUserDetails,
  uploadProfileImage,
} from "../../Services/profile.service";
import { useNavigate } from "react-router-dom";
import { logout } from "../../Slice/userSlice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { openSnackbar } from "../../Slice/snackbarSlice";
import { logoutAPI } from "../../Services/auth.service";
import { resolveAssetUrl, appendCacheBuster } from "../../Utils/url";

const selectGrade = [
  { label: "Grade 9", value: 9 },
  { label: "Grade 10", value: 10 },
  { label: "Grade 11", value: 11 },
  { label: "Grade 12", value: 12 }
];

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#4F46E5",
        ...theme.applyStyles("dark", {
          backgroundColor: "#177ddc",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

const UserProfile = () => {
  const navigate = useNavigate();
  const userId = useAppSelector((state: RootState) => state.user.uid);
  const user = useAppSelector((state: RootState) => state.user.user);
  const dispatch = useAppDispatch();
  useEffect(() => {
      dispatch(getUserDetails(localStorage.getItem("id")))
        .unwrap()  ;
    }, []);
  const [selectedGrade, setSelectedGrade] = useState<number | null>(user?.grade);
  const [isGradeSaving, setIsGradeSaving] = useState(false);
  const [institutionValue, setInstitutionValue] = useState(user?.institution || "");
  const [isInstitutionEditing, setIsInstitutionEditing] = useState(false);
  const [isInstitutionSaving, setIsInstitutionSaving] = useState(false);
  const [UserImage, setUserImage] = useState<string>(
    user?.profile_image 
      ? resolveAssetUrl(user.profile_image)
      : UserImg 
  );
  
 

  const [selectedBirthDate, setSelectedBirthDate] = useState<Dayjs | null>(() => {
    if (user?.birth_date) {
      const parsedDate = dayjs(user.birth_date, 'MM/DD/YYYY');
      return parsedDate.isValid() ? parsedDate : null;
    }
    return null;
  });
  const [isDateSaving, setIsDateSaving] = useState(false);

  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isPasswordDisplay, setIsPasswordDisplay] = useState(false);
  useEffect(()=>{
    if(user){
      if(user.profile_image){
      setUserImage(appendCacheBuster(user.profile_image, Date.now()))}
      setSelectedGrade(user?.grade);
      setInstitutionValue(user?.institution || "");
      setSelectedBirthDate(
        user?.birth_date ? dayjs(user.birth_date, 'MM/DD/YYYY') : null
      );
    }
  },[user]);

 

  const handleGradeChange = (
    event: React.SyntheticEvent,
    newValue: { label: string; value: number } | null
  ) => {
    if (newValue && newValue.value !== selectedGrade) {
      setSelectedGrade(newValue.value);
      updateUserData({ grade: newValue.value });
    }
  };

  const handleBirthDateChange = (newValue: Dayjs | null) => {
    if (newValue && (!selectedBirthDate || !newValue.isSame(selectedBirthDate, 'day'))) {
      setSelectedBirthDate(newValue);
      updateUserData({ birth_date: newValue.format('MM/DD/YYYY') });
    }
  };

  const updateUserData = (updatedFields: { institution?: string; grade?: number; birth_date?: string }) => {
    const isInstitutionUpdate = 'institution' in updatedFields;
    const isGradeUpdate = 'grade' in updatedFields;
    const isDateUpdate = 'birth_date' in updatedFields;
    if (isInstitutionUpdate) setIsInstitutionSaving(true);
    if (isGradeUpdate) setIsGradeSaving(true);
    if (isDateUpdate) setIsDateSaving(true);

    const userDetails = {
      userId: userId,
      institution: isInstitutionUpdate ? updatedFields.institution : (user?.institution || "").trim(),
      grade: isGradeUpdate ? updatedFields.grade : selectedGrade,
      birth_date: isDateUpdate ? updatedFields.birth_date : selectedBirthDate?.format('MM/DD/YYYY'),
    };

    dispatch(updateUserDetails(userDetails))
      .unwrap()
      .then(() => {
        if (isInstitutionUpdate) {
          setIsInstitutionEditing(false);
        }
        dispatch(getUserDetails(userId));
      })
      .catch((error) => {
        console.error("Error updating user data:", error);
        if (isInstitutionUpdate) {
          setInstitutionValue(user?.institution || "");
        }
      })
      .finally(() => {
        if (isInstitutionUpdate) setIsInstitutionSaving(false);
        if (isGradeUpdate) setIsGradeSaving(false);
        if (isDateUpdate) setIsDateSaving(false);
      });
  };

  const handleInstitutionEdit = () => {
    setInstitutionValue(user?.institution || "");
    setIsInstitutionEditing(true);
  };

  const handleInstitutionCancel = () => {
    setInstitutionValue(user?.institution || "");
    setIsInstitutionEditing(false);
  };

  const handleInstitutionSave = () => {
    const trimmedInstitution = institutionValue.trim();

    if (trimmedInstitution.length < 2) {
      dispatch(
        openSnackbar({
          message: "Institution must be at least 2 characters",
          severity: "error",
        })
      );
      return;
    }

    if (trimmedInstitution.length > 255) {
      dispatch(
        openSnackbar({
          message: "Institution must be 255 characters or less",
          severity: "error",
        })
      );
      return;
    }

    if (trimmedInstitution === (user?.institution || "").trim()) {
      setIsInstitutionEditing(false);
      return;
    }

    setInstitutionValue(trimmedInstitution);
    updateUserData({ institution: trimmedInstitution });
  };

  const getGradeSuffix = (grade: number) => {
    return `${grade}th`;
  };

  const handleSignOut = async() => {
    await dispatch(logoutAPI());
    await navigate("/login");
    await dispatch(logout());
  };

  const getSelectedOption = () => {
    if (selectedGrade === null || !selectGrade?.length) return null;
    return selectGrade.find((option) => option.value === selectedGrade) || null;
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file || !userId) {
      dispatch(
        openSnackbar({
          message: "Please select an image file and ensure you are logged in",
          severity: "error",
        })
      );
      return;
    }
  
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      dispatch(
        openSnackbar({
          message: "Please select a valid image file (JPEG, JPG, or PNG only)",
          severity: "error",
        })
      );
      return;
    }
  
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.jpeg', '.jpg', '.png'];
    if (!validExtensions.some(ext => fileName.endsWith(ext))) {
      dispatch(
        openSnackbar({
          message: "File extension not allowed. Please use JPEG, JPG, or PNG",
          severity: "error",
        })
      );
      return;
    }
  
    if (file.size > 5 * 1024 * 1024) {
      dispatch(
        openSnackbar({
          message: "Image size should be less than 5MB",
          severity: "error",
        })
      );
      return;
    }
  
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setUserImage(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  
    try {
      await dispatch(uploadProfileImage({
        userId: userId, 
        imageFile: file
      })).unwrap();
      

      await dispatch(getUserDetails(userId));
      dispatch(openSnackbar({ message: "Image updated successfully 🎉", severity: "success" }))
    } catch (error) {
      console.error('Image upload failed:', error);
      setUserImage(resolveAssetUrl(user?.profile_image) || UserImg);
      dispatch(
        openSnackbar({
          message: "Failed to upload image. Please try again.",
          severity: "error",
        })
      );
    }
  };


  return (
    <>
      <div className="user-profile-wrapper">
        <div>
          <div className="row gy-4">
            <div className="col-md-7 col-lg-8">
              <div className="custom-user-card">
                <div className="custom-user-card-header">
                  <div className="user-profile-block">
                  { user && ( <div className="user-img" >
                      <img src={UserImage}  alt="" />
                      <label className="camera-icon-img" htmlFor="UserProfile">
                        <img src={CameraImgIcon} alt="" />
                        <input id="UserProfile" type="file" hidden onChange={handleImageChange} accept="image/*" />
                      </label>
                    </div>) }
                    <div className="user-info">
                      {user && <div className="user-name">{user?.name}</div>}
                      <div className="user-grade">
                        {user?.grade ? `${getGradeSuffix(user.grade)} Grade Student` : "Grade not set"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="custom-user-card-body">
                  <div className="custom-user-card-body-title">
                    Profile Details
                  </div>

                  <div className="profile-details-listing">
                    <div className="details-listing-items">
                      <div className="input-form-field-wrapper" style={{ display: "flex", flexDirection: "column" }}>
                        <label htmlFor="">Email</label>
                        <TextField
                          className="out-label-input-text"
                          id="email"
                          name="email"
                          placeholder="Email"
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          type="email"
                          value={user?.email || ""}
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </div>
                    </div>
                    <div className="details-listing-items">
                      <div className="input-form-field-wrapper" style={{ display: "flex", flexDirection: "column" }}>
                        <label htmlFor="">School/Institution</label>
                        <TextField
                          className="out-label-input-text"
                          id="institution"
                          name="institution"
                          placeholder="Institution / School"
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          type="text"
                          value={institutionValue}
                          onChange={(event) => setInstitutionValue(event.target.value)}
                          disabled={isInstitutionSaving}
                          InputProps={{
                            readOnly: !isInstitutionEditing,
                            endAdornment: !isInstitutionEditing ? (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  onClick={handleInstitutionEdit}
                                  aria-label="edit institution"
                                  disabled={isInstitutionSaving}
                                >
                                  <img src={EditTextIcon} alt="Edit" style={{ width: 20, height: 20 }} />
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          }}
                        />
                        {isInstitutionEditing && (
                          <div className="institution-actions">
                            <LightButton onClick={handleInstitutionCancel} disabled={isInstitutionSaving}>
                              Cancel
                            </LightButton>
                            <PrimaryButton onClick={handleInstitutionSave} disabled={isInstitutionSaving}>
                              {isInstitutionSaving ? "Saving..." : "Save"}
                            </PrimaryButton>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="details-listing-items">
                      <div className="input-form-field-wrapper">
                        <label htmlFor="">Grade Level</label>
                        <Autocomplete
                          className="grade"
                          id="grade"
                          options={selectGrade}
                          getOptionLabel={(option) => option.label}
                          value={getSelectedOption()}
                          onChange={handleGradeChange}
                          disabled={isGradeSaving}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              className="out-label-input-text"
                              margin="normal"
                              variant="outlined"
                              fullWidth
                              placeholder="Select Grade"
                            />
                          )}
                          renderOption={(props, option) => (
                            <ListItem
                              className="autocomplete-list-items"
                              {...props}
                            >
                              <ListItemText
                                sx={{ padding: "0px", margin: "0px" }}
                                primary={option.label}
                              />
                            </ListItem>
                          )}
                        />
                      </div>
                    </div>
                    <div className="details-listing-items">
                      <div className="input-form-field-wrapper datepicker-field-wrapper">
                        <label htmlFor="">Date of Birth</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={selectedBirthDate}
                            onChange={handleBirthDateChange}
                            format="MM/DD/YYYY" 
                            disabled={isDateSaving}
                            disableFuture
                            slotProps={{
                              textField: {
                                className: "out-label-input-text",
                                margin: "normal",
                                variant: "outlined",
                                fullWidth: true,
                                placeholder: "MM/DD/YYYY",
                                inputProps: {
                                  readOnly: true,
                                }
                              },
                                popper: {
                                  placement: "bottom-end"
                                }
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                    <div className="details-listing-items">
                      <div className="input-form-field-wrapper datepicker-field-wrapper member-since-field">
                        <label htmlFor="">Member Since</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={user?.member_since ? dayjs(user.member_since) : null}
                            format="MM/DD/YYYY"
                            disabled
                            slotProps={{
                              textField: {
                                className: "out-label-input-text",
                                margin: "normal",
                                variant: "outlined",
                                fullWidth: true,
                                placeholder: "MM/DD/YYYY",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-5 col-lg-4">
              <div className="user-custom-card-group">
                <div className="custom-user-card plan-card">
                  <div className="plan-card-header">
                    <div className="card-title">Current Plan</div>
                    {user.is_paid ?
                    <Button className="active-btn">Active</Button> :
                    <Button className="free-plan-btn" disabled>Free Plan</Button>}
                  </div>
                  <div className="plan-card-body">
                    <div className="plan-details-list">
                      <div className="plan-details-list-title">
                        {user?.is_paid ? "Student" : "Upgrade to Premium for unlimited access"}
                      </div>
                      <div className="plan-details-list-items">
                        <img src={CheckIcon} alt="" />
                        <div className="details-list-items-text">
                          {user?.is_paid ? "Unlimited access to all courses" : "Unlimited tokens"}
                        </div>
                      </div>
                      <div className="plan-details-list-items">
                        <img src={CheckIcon} alt="" />
                        <div className="details-list-items-text">
                          {user?.is_paid ? "Progress tracking & certificates" : "Advanced features"}
                        </div>
                      </div>
                      <div className="plan-details-list-items">
                        <img src={CheckIcon} alt="" />
                        <div className="details-list-items-text">
                          {user?.is_paid ? "100 practice token" : "Priority support"}
                        </div>
                      </div>
                    </div>
                    <div className="plan-card-action">
                      <PrimaryButton
                        onClick={() => {
                           if(user.remaining_tokens <= 0) {
                        navigate('/price')
                            }else{
                        navigate('/dashboard/plan_details')
                      }
                        }}
                      >
                        {user?.is_paid ? "View Plan": "Upgrade Now"}
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
                <div className="custom-user-card setting-card">
                  <div className="plan-card-header">
                    <div
                      className="card-title"
                      style={{ display: isPasswordDisplay ? "none" : "block" }}
                    >
                      Setting
                    </div>
                  </div>
                  <div className="setting-card-body ">
                    {!isPasswordReset && (
                      <ChangePassword
                        handleChangePassword={setIsPasswordReset}
                        toogleDisplay={setIsPasswordDisplay}
                      />
                    )}
                    {isPasswordReset && <PasswordReset />}
                    {!isPasswordDisplay && (
                      <div className="notification-control-field">
                        Notifications
                        <div className="notification-control-btn">
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <AntSwitch
                              defaultChecked
                              inputProps={{ "aria-label": "ant design" }}
                            />
                          </Stack>
                        </div>
                      </div>
                    )}
                    {!isPasswordDisplay && (
                      <Button
                        className="sign-out-btn"
                        onClick={() => handleSignOut()}
                      >
                        Sign Out
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;
